/**
 * Robust trend estimation: Theil–Sen slope paired with the Mann–Kendall
 * monotonic-trend test.
 *
 * Both are non-parametric and outlier-resistant — a single wild spike that
 * would wreck an ordinary least-squares fit barely moves the Theil–Sen median
 * slope, and Mann–Kendall gives an honest p-value so the agent can hedge
 * ("a statistically significant rise") instead of over-reading noise.
 */
import { cumulativeStdNormalProbability, mean, median } from 'simple-statistics';

import { MS_PER_DAY } from '#core/lib/utils/date.ts';

import { EPSILON } from './constants.ts';
import type { TimeseriesPoint, TrendDirection, TrendResult } from './types.ts';

/**
 * Theil–Sen slope estimator: the median of the slopes between every pair of
 * points, expressed in value-units per day. Returns 0 for fewer than two
 * distinct-time points. O(n²) in the number of points.
 */
export function theilSenSlope(points: TimeseriesPoint[]): number {
  const slopes = points.flatMap((pi, i) =>
    points.slice(i + 1).reduce<number[]>((acc, pj) => {
      const days = (pj.timestamp - pi.timestamp) / MS_PER_DAY;
      // Skip coincident timestamps — their pairwise slope is undefined.
      if (days !== 0) {
        acc.push((pj.value - pi.value) / days);
      }
      return acc;
    }, []),
  );

  return slopes.length === 0 ? 0 : median(slopes);
}

export interface MannKendallResult {
  /** Kendall's tau-b rank correlation in [-1, 1]. */
  tau: number;
  /** Two-sided p-value for the null hypothesis of no monotonic trend. */
  pValue: number;
}

/**
 * Mann–Kendall monotonic-trend test with tie correction and a continuity
 * correction on the normal approximation. Reports Kendall's tau-b and a
 * two-sided p-value. For fewer than three points the test is undefined, so it
 * reports no trend (tau 0, p 1).
 */
export function mannKendall(points: TimeseriesPoint[]): MannKendallResult {
  const values = points.map((p) => p.value);
  const n = values.length;
  if (n < 3) {
    return { tau: 0, pValue: 1 };
  }

  // S = Σ_{i<j} sign(v_j − v_i): net count of concordant vs discordant pairs.
  const S = values.reduce(
    (outer, vi, i) => outer + values.slice(i + 1).reduce((inner, vj) => inner + Math.sign(vj - vi), 0),
    0,
  );

  // Sizes of tied value-groups, for the variance and tau-b tie corrections.
  const counts = values.reduce<Map<number, number>>((m, v) => m.set(v, (m.get(v) ?? 0) + 1), new Map());
  const tieSizes = [...counts.values()];

  const n0 = (n * (n - 1)) / 2;
  const varS = (n * (n - 1) * (2 * n + 5) - tieSizes.reduce((s, t) => s + t * (t - 1) * (2 * t + 5), 0)) / 18;

  // tau-b normalises S by the tie-adjusted pair counts. Timestamps are assumed
  // strictly increasing (no ties on the time axis), so the time-tie term is 0.
  const n1 = tieSizes.reduce((s, t) => s + (t * (t - 1)) / 2, 0);
  const denom = Math.sqrt((n0 - n1) * n0);
  const tau = denom > 0 ? S / denom : 0;

  if (varS <= 0) {
    return { tau, pValue: 1 };
  }

  // Continuity-corrected standardised score → two-sided p-value. Subtracting
  // sign(S) pulls S one unit toward zero (the correction); S == 0 gives z = 0.
  const z = S === 0 ? 0 : (S - Math.sign(S)) / Math.sqrt(varS);
  const pValue = Math.min(1, 2 * (1 - cumulativeStdNormalProbability(Math.abs(z))));

  return { tau, pValue };
}

export interface TrendOptions {
  /** Significance threshold for the Mann–Kendall test. Default 0.05. */
  alpha?: number;
}

/** Map a slope + significance verdict to a direction, gating noise to `'flat'`. */
function classifyDirection(significant: boolean, slopePerDay: number): TrendDirection {
  if (!significant || slopePerDay === 0) {
    return 'flat';
  }
  return slopePerDay > 0 ? 'increasing' : 'decreasing';
}
/** Plain-language phrasing of a trend direction for a non-technical reader. */
function trendLabel(direction: TrendDirection): string {
  if (direction === 'increasing') {
    return 'trending up';
  }
  if (direction === 'decreasing') {
    return 'trending down';
  }
  return 'holding steady';
}

/**
 * Combine the Theil–Sen slope and the Mann–Kendall significance test into a
 * single trend verdict. Direction is forced to `'flat'` unless the trend is
 * statistically significant, so the agent never narrates noise as movement.
 */
export function computeTrend(points: TimeseriesPoint[], opts: TrendOptions = {}): TrendResult {
  const alpha = opts.alpha ?? 0.05;
  const slopePerDay = theilSenSlope(points);
  const { tau, pValue } = mannKendall(points);
  const significant = pValue < alpha;

  const meanValue = points.length > 0 ? mean(points.map((p) => p.value)) : 0;
  const slopePctPerDay = Math.abs(meanValue) > EPSILON ? (slopePerDay / meanValue) * 100 : null;
  const direction = classifyDirection(significant, slopePerDay);

  const { minTs, maxTs } = points.reduce(
    (acc, p) => ({ minTs: Math.min(acc.minTs, p.timestamp), maxTs: Math.max(acc.maxTs, p.timestamp) }),
    { minTs: Number.POSITIVE_INFINITY, maxTs: Number.NEGATIVE_INFINITY },
  );
  const spanDays = points.length > 1 ? (maxTs - minTs) / MS_PER_DAY : 0;
  const windowDeltaPct = slopePctPerDay === null ? null : slopePctPerDay * spanDays;

  return {
    label: trendLabel(direction),
    slopePerDay,
    slopePctPerDay,
    windowDeltaPct,
    tau,
    pValue,
    significant,
    direction,
  };
}
